import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

const KIND_LABELS = {
  entity: "Catalogue", science: "Science Pulse", observation: "Observation",
  story: "Story", article: "Article",
};
const KIND_ORDER = ["entity", "science", "observation", "story", "article"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    http.get("/search", { params: { q: q.trim(), limit: 8 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ results: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (input.trim().length >= 2) setParams({ q: input.trim() });
  };

  const grouped = {};
  for (const r of data?.results || []) {
    (grouped[r.kind] = grouped[r.kind] || []).push(r);
  }

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="search-page">
      <SeoMeta title="Search" description="Search species, places, observations, stories and science across NaturePulse." noindex />
      <div className="max-w-[860px] mx-auto px-5 md:px-8">
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">Search the archive.</h1>
        <p className="mt-2 text-sm text-[#3A3830]">Species, catalogue pages, observations, stories, science concepts and articles — one box.</p>

        <form onSubmit={submit} className="mt-6 flex gap-2">
          <div className="flex-1 flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2.5 focus-within:border-[#1B4332]">
            <MagnifyingGlass size={16} weight="bold" className="text-[#3A3830]/60 shrink-0" />
            <input
              value={input} onChange={(e) => setInput(e.target.value)} autoFocus
              placeholder="Try 'monsoon', 'banyan', 'lichen', 'heatwave'…"
              data-testid="search-input"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <button type="submit" data-testid="search-submit"
            className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90">
            Search
          </button>
        </form>

        {loading && <div className="mt-8 font-mono-tech text-[10px] tracking-[0.3em] uppercase" data-testid="search-loading">Searching…</div>}

        {!loading && data && (
          <div className="mt-8" data-testid="search-results">
            <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70">
              {data.count} result{data.count === 1 ? "" : "s"} for “{q}”
            </div>
            {data.count === 0 ? (
              <p className="mt-4 text-sm text-[#3A3830]">Nothing matched — try a species, a weather word, or a place.</p>
            ) : (
              KIND_ORDER.filter((k) => grouped[k]).map((k) => (
                <section key={k} className="mt-6">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">{KIND_LABELS[k]}</div>
                  <div className="space-y-2">
                    {grouped[k].map((r, i) => (
                      <Link key={`${k}-${i}`} to={r.url} data-testid={`search-result-${k}-${i}`}
                        className="group flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-white px-4 py-3 hover:border-[#1B4332] transition-colors">
                        <div className="min-w-0">
                          <div className="font-serif-display text-lg leading-tight truncate">{r.title}</div>
                          {r.sub && <div className="mt-0.5 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#3A3830]/60 truncate">{r.sub}</div>}
                        </div>
                        <ArrowRight size={12} weight="bold" className="shrink-0 text-[color:var(--np-brand,#B85F2A)] group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
}
